import React, { useState } from 'react'
import { Box, Button, Divider, CircularProgress, Icon } from '@mui/material'
import { Check as CheckIcon, Clear as ClearIcon } from '@mui/icons-material'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { useStyles } from './SlidesStyles'

export interface SlideHandlingBaseProps {
  userId: string
  title: string
  buttonText: string
  dialogTitle: string
  dialogPrompt: string
  buttonIcon: string
  inputLabel?: string
  maxCaseIdLength?: number
  onSubmit: (caseId: string, isScannerMode: boolean) => Promise<void>
  onClose?: () => void
  successMessage?: (caseId: string) => string
  errorMessage?: string
  isSuccess?: boolean
  isError?: boolean
  loading?: boolean
  buttonClassName?: string
}

export const SlideHandlingBase: React.FC<SlideHandlingBaseProps> = ({
  userId,
  title,
  buttonText,
  dialogTitle,
  dialogPrompt,
  buttonIcon,
  inputLabel = 'Barcode TRF ID (5 characters only)',
  maxCaseIdLength = 5,
  onSubmit,
  onClose,
  successMessage = (caseId) => `Scanning ${caseId} is successful. You may scan another value or click close to exit.`,
  errorMessage,
  isSuccess = false,
  isError = false,
  loading = false,
  buttonClassName,
}) => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const [caseId, setCaseId] = useState('')
  const [caseIdValue, setCaseIdValue] = useState('')
  const [isLoading, setIsLoading] = useState(loading)
  const [manualMaxLength, setManualMaxLength] = useState(0)
  const [lettersDetected, setLettersDetected] = useState('')
  const [numbersStarted, setNumbersStarted] = useState(false)
  const [formatDebug, setFormatDebug] = useState('')
  const [validationError, setValidationError] = useState('')
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
  const [openSuccessSnackBar, setOpenSuccessSnackBar] = useState(false)
  const [openErrorSnackBar, setOpenErrorSnackBar] = useState(false)

  // Reset all state when dialog opens
  const handleOpen = () => {
    // Reset success/error states
    setOpenSuccessSnackBar(false)
    setOpenErrorSnackBar(false)
    // Reset input and tracking states
    setCaseId('')
    setCaseIdValue('')
    setManualMaxLength(0)
    setLettersDetected('')
    setNumbersStarted(false)
    setFormatDebug('')
    setValidationError('')
    setHasAttemptedSubmit(false)
    setIsLoading(false)
    // Finally, open the dialog
    setOpen(true)
  }

  // Reset all state when dialog closes
  const handleClose = () => {
    setOpen(false)
    setCaseId('')
    setCaseIdValue('')
    setManualMaxLength(0)
    setLettersDetected('')
    setNumbersStarted(false)
    setFormatDebug('')
    setValidationError('')
    setHasAttemptedSubmit(false)
    setOpenSuccessSnackBar(false)
    setOpenErrorSnackBar(false)
    setIsLoading(false)

    // Call parent onClose if provided
    if (onClose) {
      onClose()
    }
  }

  // Helper function to validate input - max 2 alphabets and must be followed by numbers
  const validateInput = (val) => {
    // Clear any previous validation errors
    setValidationError('')

    // Empty input is valid
    if (!val) return true

    // Check if input has more than 2 alphabets at the beginning
    const tooManyLetters = /^[A-Za-z]{3,}/
    if (tooManyLetters.test(val)) {
      setValidationError('Maximum 2 alphabets allowed')
      return false
    }

    // Special case: Always allow just letters (up to 2)
    if (/^[A-Za-z]{1,2}$/.test(val)) {
      return true
    }

    // Allow valid pattern: 1-2 letters followed by only digits
    if (/^[A-Za-z]{1,2}\d+$/.test(val)) {
      return true
    }

    // Reject any other pattern that doesn't match our requirements
    if (!/^[A-Za-z]{1,2}\d+$/.test(val)) {
      setValidationError('Invalid format. Use 1-2 letters followed by numbers only')
      return false
    }

    return true
  }

  // Detect pattern format (W or WW)
  const detectFormat = (val) => {
    if (/^[A-Za-z]\d*$/.test(val)) return 'W'
    if (/^[A-Za-z]{2}\d*$/.test(val)) return 'WW'
    return ''
  }

  // Handle case ID input changes
  const onCaseIdChange = (val) => {
    // Reset success/error messages
    if (openSuccessSnackBar) {
      setOpenSuccessSnackBar(false)
      setOpenErrorSnackBar(false)
    }

    // Validate input
    if (!validateInput(val)) {
      // Don't update if invalid input
      return
    }

    // Update input value
    setCaseId(val)

    // Track letter sequence before numbers start
    const letterPattern = /^[A-Za-z]+$/
    const numberPattern = /^[A-Za-z]+\d+$/
    const currentFormat = detectFormat(val)

    // If format has changed, reset state
    if (currentFormat && currentFormat !== formatDebug) {
      setFormatDebug(currentFormat)
      setLettersDetected(val.match(/^[A-Za-z]+/)?.[0] || '')
      setNumbersStarted(false)
      setManualMaxLength(0)
    }

    // Reset if the pattern changes (e.g., from W1234 to WW)
    if (letterPattern.test(val) && (lettersDetected !== val || (numbersStarted && !numberPattern.test(val)))) {
      setLettersDetected(val)
      setNumbersStarted(false)
      setManualMaxLength(0) // Reset max length when letter pattern changes
    }

    // If we're still in the letters-only phase
    if (letterPattern.test(val) && !numbersStarted) {
      setLettersDetected(val)
    }

    // Once numbers start appearing after letters
    if (numberPattern.test(val) && !numbersStarted) {
      setNumbersStarted(true)
      // Set max length based on letter count
      setManualMaxLength(maxCaseIdLength)
    }

    // If max length reached, make API call
    if (manualMaxLength > 0 && val.length >= manualMaxLength) {
      const finalVal = val.substring(0, manualMaxLength)
      setCaseIdValue(finalVal)
      handleSubmit(finalVal, false)
    }
  }

  const handleSubmit = async (val, scannerMode) => {
    // Mark that we've attempted a submission
    setHasAttemptedSubmit(true)
    // Reset any previous error states
    setOpenErrorSnackBar(false)
    setIsLoading(true)

    try {
      await onSubmit(val, scannerMode)
      setOpenSuccessSnackBar(true)
      // Always clear the input field after submission
      setCaseId('')
    } catch (error) {
      setOpenErrorSnackBar(true)
      // Clear the input field after failed submission
      setCaseId('')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box className={`${classes.mt} , ${classes.mb}`}>
      <Button variant="contained" onClick={handleOpen} className={`${classes.btn} ${buttonClassName || ''}`}>
        <Icon className={classes.sendIcon}>{buttonIcon}</Icon> {buttonText}
      </Button>

      <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
        <DialogTitle className={classes.dialogTitle}>{dialogTitle}</DialogTitle>
        <Divider />
        <DialogContent>
          <DialogContentText className={classes.dialogContent}>{dialogPrompt}</DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label={inputLabel}
            onChange={(e) => onCaseIdChange(e.target.value)}
            onPaste={(e) => {
              e.preventDefault()
              const pastedValue = e.clipboardData.getData('text')
              onCaseIdChange(pastedValue)
            }}
            value={caseId}
            type="text"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            style={{ width: '250px' }}
            inputProps={{
              maxLength: manualMaxLength > 0 ? manualMaxLength : undefined,
            }}
            error={!!validationError}
            helperText={validationError}
          />
          {isLoading && (
            <CircularProgress className={classes.loadingIndicator} style={{ width: '30px', height: '30px' }} />
          )}
          <div className={classes.inlineMessage}>
            {openSuccessSnackBar && isSuccess && <p className={classes.success}>{successMessage(caseIdValue)}</p>}
            {/* Only show error if we have attempted a submission and there's an error condition */}
            {hasAttemptedSubmit && caseIdValue && (openErrorSnackBar || isError) && errorMessage && (
              <p className={classes.error}>{errorMessage}</p>
            )}
          </div>
        </DialogContent>
        <DialogActions className={classes.buttonActions}>
          <Button onClick={handleClose} variant="outlined" className={classes.buttonMargin} endIcon={<ClearIcon />}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
